'use client';

import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Input, Button, Typography, Alert, Space } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { notify } from '@utils/notify';
import authApi from '@apis/auth';
import { useCookieAuth } from '@hooks/useCookieAuth';

const { Text, Paragraph } = Typography;

interface OtpVerificationProps {
  email: string;
  onVerificationSuccess: () => void;
  onBack: () => void;
}

// Validation schema for OTP
const otpSchema = Yup.object().shape({
  otp: Yup.string()
    .matches(/^\d+$/, 'Mã OTP chỉ được chứa số')
    .length(6, 'Mã OTP phải có 6 số')
    .required('Mã OTP là bắt buộc'),
});

export function OtpVerification({ email, onVerificationSuccess, onBack }: OtpVerificationProps) {
  const { setToken } = useCookieAuth();
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);

  const formik = useFormik({
    initialValues: {
      otp: '',
    },
    validationSchema: otpSchema,
    onSubmit: async (values) => {
      setVerificationError(null);
      try {
        setVerificationError(null);
        const user = await authApi.verifyOtp({
          email: email?.trim(),
          otp: values.otp?.trim(),
        });
        setToken(user.data.accessToken, 'access_token');
        setToken(user.data.refreshToken, 'refresh_token');
        onVerificationSuccess();
        notify.success('Đăng ký thành công');
      } catch (error) {
        setVerificationError('Mã OTP không đúng. Vui lòng thử lại.');
      }
    },
  });

  const handleResendOtp = async () => {
    setIsResending(true);

    authApi.resendOtp({
      email: email?.trim(),
    });

    setTimeout(() => {
      setIsResending(false);
    }, 1500);
  };

  return (
    <div className="py-2">
      <div className="mb-4 flex items-center">
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack} className="p-0 pr-2" />
        <Text strong className="text-base">
          Xác thực OTP
        </Text>
      </div>

      <Paragraph type="secondary" className="mb-4">
        Chúng tôi đã gửi mã xác thực 6 số đến email <Text strong>{email}</Text>. Vui lòng nhập mã để
        tiếp tục.
      </Paragraph>

      {verificationError && <Alert message={verificationError} type="error" className="mb-4" />}

      <form onSubmit={formik.handleSubmit}>
        <div className="mb-4">
          <div className="text-sm font-medium mb-1">Mã xác thực OTP</div>
          <Input
            id="otp"
            name="otp"
            placeholder="Nhập mã 6 số"
            value={formik.values.otp}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            maxLength={6}
            className="text-center text-lg tracking-widest h-12"
            status={formik.touched.otp && formik.errors.otp ? 'error' : ''}
          />
          {formik.touched.otp && formik.errors.otp && (
            <div className="text-red-500 text-sm mt-1">{formik.errors.otp}</div>
          )}
        </div>

        <div className="mb-4">
          <Button
            type="primary"
            htmlType="submit"
            className="!h-12"
            loading={formik.isSubmitting}
            disabled={!formik.isValid || formik.isSubmitting}
            block
          >
            {formik.isSubmitting ? 'Đang xử lý...' : 'Xác thực'}
          </Button>
        </div>
      </form>

      <div className="text-center mt-4">
        <Space direction="vertical" size={8}>
          <Text type="secondary">
            Không nhận được mã?{' '}
            <button
              onClick={handleResendOtp}
              disabled={isResending || formik.isSubmitting}
              className="p-0 text-blue-500"
            >
              {isResending ? 'Đang gửi...' : 'Gửi lại'}
            </button>
          </Text>
        </Space>
      </div>
    </div>
  );
}
